<section class="aged-strategy" aria-labelledby="aged-buckets-heading">
  <div class="section-heading">
    <h2 id="aged-buckets-heading">Days on Lot Discounts</h2>
    <p>Set a discount for each inclusive day range. Minimum days follow the preceding bucket.</p>
  </div>

  <form [formGroup]="bucketForm" novalidate>
    <div class="table-scroll" role="region" aria-label="Days on lot bucket configuration" tabindex="0">
      <table>
        <caption class="visually-hidden">Day ranges and their configured discounts</caption>
        <thead>
          <tr>
            <th scope="col">Minimum Days</th>
            <th scope="col">Maximum Days</th>
            <th scope="col">Discount Type</th>
            <th scope="col">Discount Value</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody formArrayName="buckets">
          @for (group of bucketControls.controls; track group; let index = $index) {
            <tr [formGroupName]="index">
              <td>
                <label class="visually-hidden" [for]="'age-minimum-' + index">Bucket {{ index + 1 }} Minimum Days</label>
                <input [id]="'age-minimum-' + index" type="text" [value]="rows[index].minimum ?? '—'" readonly>
              </td>
              <td>
                <label class="visually-hidden" [for]="'age-maximum-' + index">Bucket {{ index + 1 }} Maximum Days</label>
                <input [id]="'age-maximum-' + index" type="text" inputmode="numeric" formControlName="maximum"
                  placeholder="No maximum" [attr.aria-invalid]="rows[index].rangeError !== ''"
                  [attr.aria-describedby]="'age-range-error-' + index">
                <span class="field-error" [id]="'age-range-error-' + index">{{ rows[index].rangeError }}</span>
              </td>
              <td>
                <label class="visually-hidden" [for]="'age-discount-type-' + index">Bucket {{ index + 1 }} Discount Type</label>
                <div class="discount-type-select">
                  <select [id]="'age-discount-type-' + index" formControlName="discountType">
                    <option value="percentage">Percentage</option>
                    <option value="dollar">Dollar</option>
                  </select>
                  <i class="pi pi-chevron-down" aria-hidden="true"></i>
                </div>
              </td>
              <td>
                <label class="visually-hidden" [for]="'age-discount-value-' + index">Bucket {{ index + 1 }} Discount Value</label>
                <div class="discount-input">
                  <input [id]="'age-discount-value-' + index" type="text" inputmode="decimal" formControlName="discountValue"
                    [attr.aria-invalid]="rows[index].valueError !== ''" [attr.aria-describedby]="'age-value-error-' + index">
                  @if (group.controls.discountType.value === 'percentage') { <span aria-hidden="true">%</span> }
                </div>
                <span class="field-error" [id]="'age-value-error-' + index">{{ rows[index].valueError }}</span>
              </td>
              <td>
                <button type="button" class="remove-button" [attr.aria-label]="'Remove bucket ' + (index + 1)"
                  (click)="removeBucket(index)">Remove</button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (rows.length === 0) {
      <p class="field-error" role="alert">Add at least one day range before staging this rule.</p>
    }
    <div class="bucket-actions">
      <button type="button" class="add-button" [disabled]="addBucketGuidance !== ''"
        aria-describedby="add-bucket-guidance" (click)="addBucket()">Add Bucket</button>
      <span id="add-bucket-guidance" class="field-help">{{ addBucketGuidance }}</span>
    </div>
  </form>

  <section class="strategy-preview" aria-labelledby="aged-preview-heading">
    <h3 id="aged-preview-heading">Strategy Preview</h3>
    <p class="strategy-settings">
      Percentage basis: <strong>{{ basisLabel }}</strong>.
      Max Discount Value:
      @if (!capValid) {
        <strong class="field-error">Needs correction</strong>
      } @else if (maxDiscountAmount === null) {
        <strong>No maximum</strong>
      } @else {
        <strong>{{ maxDiscountAmount | currency:'USD':'symbol':'1.2-2' }}</strong>
      }
    </p>
    @if (showDollarPreviews) {
      <p class="field-help">These values are based on a {{ maximumPrice | currency:'USD':'symbol':'1.0-2' }} maximum price.</p>
    } @else {
      <p class="field-help">Enter a valid Maximum Price and optional cap to display illustrative dollar discounts.</p>
    }
    <div class="table-scroll">
      <table>
        <caption class="visually-hidden">Review the complete configured strategy</caption>
        <thead>
          <tr>
            <th scope="col">Days on Lot</th>
            <th scope="col">Configured Discount</th>
            @if (showDollarPreviews) { <th scope="col">Illustrative discount at Maximum Price</th> }
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track $index) {
            <tr>
              <td>{{ row.rangeLabel }}</td>
              <td>
                @if (row.valueError !== '') {
                  <span class="field-error">Discount needs correction</span>
                } @else if (row.bucket.discountType === 'percentage') {
                  {{ row.bucket.discountValue | number:'1.0-2' }}% of {{ basisLabel }}
                } @else {
                  {{ row.bucket.discountValue | currency:'USD':'symbol':'1.2-2' }}
                }
              </td>
              @if (showDollarPreviews) {
                <td>
                  @if (row.previewAmount !== null) {
                    {{ row.previewAmount | currency:'USD':'symbol':'1.2-2' }}
                    @if (row.capped) { <span class="cap-indicator">Capped</span> }
                  } @else {
                    <span class="field-help">Correct this bucket</span>
                  }
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
    <p class="field-help">Percentage examples use Maximum Price for either basis. Actual discounts depend on vehicle pricing.</p>
  </section>
</section>
